import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { LayoutDashboard, MessageSquareText, Percent, Send, Store } from 'lucide-react'
import { fetchEmpresa, fetchEmpresaDashboard } from '../../api'
import { useAuth } from '../../AuthContext'
import { PageHeader } from '../../components/PageHeader'
import { DashboardBarChart } from '../../components/DashboardBarChart'
import { Card, CardContent } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/skeleton'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table'

const STAT_ACCENTS = {
  conversations: 'from-violet-500/20 text-violet-600 ring-violet-500/30 dark:text-violet-300',
  received: 'from-sky-500/20 text-sky-600 ring-sky-500/30 dark:text-sky-300',
  responded: 'from-emerald-500/20 text-emerald-600 ring-emerald-500/30 dark:text-emerald-300',
  rate: 'from-amber-500/20 text-amber-600 ring-amber-500/30 dark:text-amber-300',
}

const PRESETS = [
  { label: '7 días', days: 7 },
  { label: '30 días', days: 30 },
  { label: '90 días', days: 90 },
]

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function presetRange(days: number): { from: string; to: string } {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - (days - 1))
  return { from: toIsoDate(from), to: toIsoDate(to) }
}

function StatCard({
  icon: Icon,
  accent,
  value,
  label,
}: {
  icon: typeof MessageSquareText
  accent: string
  value: string | number | undefined
  label: string
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 py-5">
        <div className={`flex size-12 shrink-0 items-center justify-center rounded-xl bg-linear-to-br ring-1 ${accent}`}>
          <Icon className="size-5" strokeWidth={2} />
        </div>
        <div className="flex flex-col gap-0.5">
          <p className="text-2xl leading-none font-semibold tracking-tight">{value ?? '—'}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  )
}

export function EmpresaDashboardPage() {
  const { user, activeSociedadId } = useAuth()
  const empresaId = user?.sociedades.find((s) => s.id === activeSociedadId)?.empresa_id

  const [activePreset, setActivePreset] = useState(30)
  const [range, setRange] = useState(() => presetRange(30))
  const [sociedadId, setSociedadId] = useState<string>('all')

  const empresaQuery = useQuery({
    queryKey: ['empresa', empresaId],
    queryFn: () => fetchEmpresa(empresaId!),
    enabled: !!empresaId,
  })

  const dashboardQuery = useQuery({
    queryKey: ['empresa-dashboard', empresaId, range.from, range.to, sociedadId],
    queryFn: () =>
      fetchEmpresaDashboard(empresaId!, {
        dateFrom: range.from,
        dateTo: range.to,
        sociedadId: sociedadId !== 'all' ? sociedadId : undefined,
      }),
    enabled: !!empresaId,
  })

  function applyPreset(days: number): void {
    setActivePreset(days)
    setRange(presetRange(days))
  }

  const data = dashboardQuery.data
  const responseRate = useMemo(() => {
    if (!data || data.total_messages_received === 0) return null
    return Math.round((data.total_messages_responded / data.total_messages_received) * 100)
  }, [data])

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={LayoutDashboard}
        title="Dashboard"
        description="Actividad de todas tus Sociedades en un solo lugar."
      />

      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 py-4">
          <div className="flex items-center gap-1.5">
            {PRESETS.map((p) => (
              <Button
                key={p.days}
                type="button"
                size="sm"
                variant={activePreset === p.days ? 'default' : 'outline'}
                onClick={() => applyPreset(p.days)}
              >
                {p.label}
              </Button>
            ))}
          </div>

          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={range.from}
              max={range.to}
              onChange={(e) => {
                setActivePreset(0)
                setRange((r) => ({ ...r, from: e.target.value }))
              }}
              className="h-8 rounded-md border border-input bg-transparent px-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <span className="text-sm text-muted-foreground">a</span>
            <input
              type="date"
              value={range.to}
              min={range.from}
              max={toIsoDate(new Date())}
              onChange={(e) => {
                setActivePreset(0)
                setRange((r) => ({ ...r, to: e.target.value }))
              }}
              className="h-8 rounded-md border border-input bg-transparent px-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          <div className="ml-auto w-full sm:w-56">
            <Select value={sociedadId} onValueChange={setSociedadId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Todas las Sociedades" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las Sociedades</SelectItem>
                {empresaQuery.data?.sociedades.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Store}
          accent={STAT_ACCENTS.conversations}
          value={data?.total_conversations}
          label="Conversaciones activas"
        />
        <StatCard
          icon={MessageSquareText}
          accent={STAT_ACCENTS.received}
          value={data?.total_messages_received}
          label="Mensajes recibidos"
        />
        <StatCard icon={Send} accent={STAT_ACCENTS.responded} value={data?.total_messages_responded} label="Respondidos" />
        <StatCard
          icon={Percent}
          accent={STAT_ACCENTS.rate}
          value={responseRate === null ? '—' : `${responseRate}%`}
          label="Tasa de respuesta"
        />
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground">Mensajes por día</h2>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="size-2 rounded-full bg-indigo-500" /> Recibidos
              </span>
              <span className="flex items-center gap-1.5">
                <span className="size-2 rounded-full bg-emerald-500" /> Respondidos
              </span>
            </div>
          </div>
          {dashboardQuery.isLoading ? (
            <Skeleton className="h-56 w-full" />
          ) : (
            <DashboardBarChart data={data?.daily ?? []} />
          )}
        </CardContent>
      </Card>

      <Card className="overflow-hidden py-0">
        <div className="border-b p-4">
          <h2 className="text-sm font-semibold text-foreground">Desglose por Sociedad</h2>
          <p className="text-xs text-muted-foreground">Conversaciones con actividad en el período seleccionado.</p>
        </div>

        {dashboardQuery.isLoading && (
          <div className="flex flex-col gap-3 p-5">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-5/6" />
          </div>
        )}
        {dashboardQuery.isError && <p className="p-5 text-sm text-destructive">No se pudo cargar el dashboard.</p>}
        {data && data.by_sociedad.length === 0 && (
          <p className="p-5 text-sm text-muted-foreground">Sin actividad en el período seleccionado.</p>
        )}
        {data && data.by_sociedad.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sociedad</TableHead>
                <TableHead>Conversaciones</TableHead>
                <TableHead>Recibidos</TableHead>
                <TableHead>Respondidos</TableHead>
                <TableHead className="text-right">Tasa de respuesta</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.by_sociedad.map((s) => {
                const rate = s.messages_received === 0 ? null : Math.round((s.messages_responded / s.messages_received) * 100)
                return (
                  <TableRow key={s.sociedad_id}>
                    <TableCell className="font-medium">{s.sociedad_nombre}</TableCell>
                    <TableCell>{s.conversations}</TableCell>
                    <TableCell>{s.messages_received}</TableCell>
                    <TableCell>{s.messages_responded}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={rate === null ? 'secondary' : rate >= 80 ? 'outline' : 'destructive'}>
                        {rate === null ? '—' : `${rate}%`}
                      </Badge>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  )
}
